import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  MapPinIcon,
  TruckIcon,
  ClockIcon,
  ArrowPathIcon,
  EyeIcon,
  PlayIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

// Interfaces
interface EnvioEnVivo {
  id: string
  numeroGuia: string
  estado: 'pendiente' | 'en_transito' | 'entregado' | 'cancelado' | 'devuelto'
  conductor: string
  vehiculo: string
  origen: string
  destino: string
  ubicacionActual: string
  velocidad: number
  distanciaRestante: number
  tiempoEstimado: string
  ultimaActualizacion: string
  prioridad: 'alta' | 'media' | 'baja'
  alertas?: string[]
}

const PaginaSeguimientoEnVivo: React.FC = () => {
  const navigate = useNavigate()
  const [enviosEnVivo, setEnviosEnVivo] = useState<EnvioEnVivo[]>([])
  const [seguimientoActivo, setSeguimientoActivo] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [busqueda, setBusqueda] = useState('')
  const [actualizando, setActualizando] = useState(false)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date())
  const [, setNotificaciones] = useState<string[]>([])

  // Datos simulados
  useEffect(() => {
    const enviosIniciales: EnvioEnVivo[] = [
      {
        id: '1',
        numeroGuia: 'LX001234',
        estado: 'en_transito',
        conductor: 'Carlos Rodríguez',
        vehiculo: 'ABC-123',
        origen: 'Bogotá',
        destino: 'Medellín',
        ubicacionActual: 'Autopista Norte, Km 45',
        velocidad: 65,
        distanciaRestante: 120,
        tiempoEstimado: '2h 15min',
        ultimaActualizacion: '2 min',
        prioridad: 'alta',
        alertas: ['Tráfico moderado']
      },
      {
        id: '2',
        numeroGuia: 'LX001235',
        estado: 'en_transito',
        conductor: 'Ana García',
        vehiculo: 'DEF-456',
        origen: 'Cali',
        destino: 'Barranquilla',
        ubicacionActual: 'Vía Panamericana, Km 180',
        velocidad: 70,
        distanciaRestante: 450,
        tiempoEstimado: '6h 30min',
        ultimaActualizacion: '1 min',
        prioridad: 'media'
      },
      {
        id: '3',
        numeroGuia: 'LX001236',
        estado: 'en_transito',
        conductor: 'Miguel Torres',
        vehiculo: 'GHI-789',
        origen: 'Medellín',
        destino: 'Cartagena',
        ubicacionActual: 'Autopista al Mar, Km 25',
        velocidad: 55,
        distanciaRestante: 380,
        tiempoEstimado: '7h 45min',
        ultimaActualizacion: '5 min',
        prioridad: 'baja',
        alertas: ['Velocidad reducida', 'Zona de obras']
      }
    ]
    setEnviosEnVivo(enviosIniciales)
  }, [])

  // Actualización automática en tiempo real
  useEffect(() => {
    if (!seguimientoActivo) return

    const interval = setInterval(() => {
      setEnviosEnVivo(prev => prev.map(envio => ({
        ...envio,
        velocidad: Math.max(0, envio.velocidad + (Math.random() - 0.5) * 10),
        distanciaRestante: Math.max(0, envio.distanciaRestante - Math.random() * 5),
        ultimaActualizacion: Math.floor(Math.random() * 5) + ' min'
      })))
      setUltimaActualizacion(new Date())
    }, 30000) // Actualizar cada 30 segundos

    return () => clearInterval(interval)
  }, [seguimientoActivo])

  // Filtrar envíos
  const enviosFiltrados = enviosEnVivo.filter(envio => {
    const coincideBusqueda = envio.numeroGuia.toLowerCase().includes(busqueda.toLowerCase()) ||
                            envio.conductor.toLowerCase().includes(busqueda.toLowerCase()) ||
                            envio.vehiculo.toLowerCase().includes(busqueda.toLowerCase())
    
    const coincideEstado = filtroEstado === 'todos' || envio.estado === filtroEstado
    
    return coincideBusqueda && coincideEstado
  })

  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'en_transito': return TruckIcon
      case 'entregado': return CheckCircleIcon
      case 'cancelado': return XCircleIcon
      case 'pendiente': return ClockIcon
      default: return InformationCircleIcon
    }
  }

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'en_transito': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'entregado': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'cancelado': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'pendiente': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const obtenerColorPrioridad = (prioridad: string) => {
    switch (prioridad) {
      case 'alta': return 'border-l-red-500'
      case 'media': return 'border-l-yellow-500'
      case 'baja': return 'border-l-green-500'
      default: return 'border-l-gray-500'
    }
  }

  const actualizarManual = async () => {
    setActualizando(true)
    await new Promise(resolve => setTimeout(resolve, 1500))
    setUltimaActualizacion(new Date())
    setActualizando(false)
    setNotificaciones(prev => ['Datos actualizados manualmente', ...prev.slice(0, 4)])
  }

  const toggleSeguimiento = () => {
    setSeguimientoActivo(!seguimientoActivo)
    setNotificaciones(prev => [
      seguimientoActivo ? 'Seguimiento automático pausado' : 'Seguimiento automático activado',
      ...prev.slice(0, 4)
    ])
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <MapPinIcon className="h-8 w-8 text-blue-500 mr-3" />
                Seguimiento en Vivo
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Monitoreo en tiempo real de todos los envíos activos
              </p>
            </div>
            
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Última actualización: {ultimaActualizacion.toLocaleTimeString('es-ES')}
              </div>
              
              <button
                onClick={toggleSeguimiento}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                  seguimientoActivo
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {seguimientoActivo ? (
                  <PauseIcon className="h-4 w-4 mr-2" />
                ) : (
                  <PlayIcon className="h-4 w-4 mr-2" />
                )}
                {seguimientoActivo ? 'Pausar' : 'Activar'}
              </button>
              
              <button
                onClick={actualizarManual}
                disabled={actualizando}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${actualizando ? 'animate-spin' : ''}`} />
                {actualizando ? 'Actualizando...' : 'Actualizar'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filtros y búsqueda */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por guía, conductor o vehículo..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="todos">Todos los estados</option>
                <option value="en_transito">En tránsito</option>
                <option value="pendiente">Pendiente</option>
                <option value="entregado">Entregado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                seguimientoActivo
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
              }`}>
                {seguimientoActivo ? 'En vivo' : 'Pausado'}
              </span>
              <span>{enviosFiltrados.length} envíos activos</span>
            </div>
          </div>
        </motion.div>

        {/* Lista de envíos en vivo */}
        <div className="grid gap-6">
          {enviosFiltrados.map((envio, index) => {
            const IconoEstado = obtenerIconoEstado(envio.estado)
            
            return (
              <motion.div
                key={envio.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border-l-4 ${obtenerColorPrioridad(envio.prioridad)} hover:shadow-xl transition-shadow cursor-pointer`}
                onClick={() => navigate(`/dashboard/envios/${envio.id}/seguimiento`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <IconoEstado className="h-6 w-6 text-blue-500" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {envio.numeroGuia}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${obtenerColorEstado(envio.estado)}`}>
                        {envio.estado.replace('_', ' ').toUpperCase()}
                      </span>
                      {envio.prioridad === 'alta' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          ALTA PRIORIDAD
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Conductor</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{envio.conductor}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Vehículo</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{envio.vehiculo}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Ruta</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{envio.origen} → {envio.destino}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Ubicación actual</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{envio.ubicacionActual}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm">
                      <div className="flex items-center space-x-1">
                        <TruckIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">{Math.round(envio.velocidad)} km/h</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPinIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">{Math.round(envio.distanciaRestante)} km restantes</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">ETA: {envio.tiempoEstimado}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ArrowPathIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">Actualizado hace {envio.ultimaActualizacion}</span>
                      </div>
                    </div>
                    
                    {envio.alertas && envio.alertas.length > 0 && (
                      <div className="mt-3 flex items-center space-x-2">
                        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                        <div className="flex flex-wrap gap-2">
                          {envio.alertas.map((alerta, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            >
                              {alerta}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4">
                    <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {enviosFiltrados.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No hay envíos activos
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              No se encontraron envíos que coincidan con los filtros seleccionados.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default PaginaSeguimientoEnVivo
